/**
 * free-samples.js — Memree sample chooser + rules engine (Option A, Cart Journey rework 8 July 2026)
 *
 * Ben Rutter's 'Cart Journey' design. Three theme-native elements in the drawer:
 *  - #CartDrawer-CrossSell      — FREE chooser (£0 products). Heading swaps by allowance
 *    ('Choose a free sample' / 'Choose two free samples') with an 'X / N selected'
 *    counter pill. HIDES once the allowance is used (no dim-when-full).
 *  - #CartDrawer-CrossSellPaid  — PAID chooser (£5 products) with a close (X). Opens
 *    only from the 'Add a sample' button; open/closed state survives re-renders.
 *  - #CartDrawer-AddSample      — 'Add a sample' button in the cart items area.
 *    Visible only when the customer has no free samples available (cart value, or a
 *    blocked voucher) or has used the whole free allowance — and the paid chooser
 *    is closed. Hidden on an empty cart.
 *
 * On every cart change the engine:
 *  - Reads /cart.js and computes the free allowance from the PRODUCT subtotal
 *    (pre-voucher; sample and 2ml lines excluded): at/over tier1 -> 1, tier2 -> 2.
 *  - A blocked voucher (MEMREE10-/MEMREE20-) sets the allowance to 0: the free
 *    chooser hides and chosen FREE samples are removed. The £5 paid route stays
 *    available unless voucher_blocks_paid is true (decision 8 July 2026).
 *  - Removes free samples that no longer qualify, re-renders the drawer via the
 *    Section Rendering API and re-applies all visibility states.
 *
 * Cards toggle: ADD TO CART adds the £0/£5 line ('ADDED' + tick state); tapping
 * again while visible removes it. Chosen sample lines can also be removed with the
 * line's bin icon — freeing a slot brings the free chooser back automatically.
 * The matched same-scent 2ml (Shopify Bundle) is NOT handled here.
 *
 * Config comes from <script id="sampleLogicJSON"> in snippets/cart-free-samples.liquid.
 * No dependency on BOGOS/Kite. Safe to run on a clean store.
 */
(function () {
  'use strict';

  // ---- Property keys written onto chosen sample lines ----
  var P_SAMPLE = '_sample';          // marks any sample line
  var P_CHOICE = '_sample_choice';   // marks a sample the customer chose in the chooser
  var P_TIER = '_sample_tier';       // 'free' (£0) or 'addon' (£5)

  // ---- Element ids ----
  var FREE_ID = 'CartDrawer-CrossSell';
  var PAID_ID = 'CartDrawer-CrossSellPaid';
  var BTN_ID = 'CartDrawer-AddSample';

  var DEFAULTS = {
    show_logs: false,
    free_samples_collection: 'free-samples',
    paid_samples_collection: 'paid-samples',
    tier1_threshold: 6000,   // pence — AT OR OVER this => 1 free sample (£60)
    tier2_threshold: 10000,  // pence — AT OR OVER this => 2 free samples (£100)
    addon_price: 500,        // pence — price of a paid extra sample
    blocked_code_prefixes: ['MEMREE10-', 'MEMREE20-'], // £10 / £20 vouchers withhold FREE samples
    blocked_discount_values: [1000, 2000], // pence — secondary check on £10 / £20 amounts
    threshold_strictly_greater: false,     // false = 'at or above' (£60 / £100 exactly qualify)
    voucher_blocks_paid: false,            // false = paid £5 route stays available with a blocked voucher
    free_heading_one: 'Choose a free sample',
    free_heading_two: 'Choose two free samples',
    counter_suffix: 'selected',
    marquee_speed: 24 // px per second — idle carousel drift (0 disables)
  };

  var CFG = readConfig();
  var lock = false;
  var booted = false;
  var paidOpen = false; // paid chooser open/closed — survives drawer re-renders
  var driftKilled = false; // first touch on a carousel parks the idle drift for the page session (Joe, 8 July 2026)

  function readConfig() {
    try {
      var el = document.getElementById('sampleLogicJSON');
      if (!el) return DEFAULTS;
      var parsed = JSON.parse(el.textContent);
      return Object.assign({}, DEFAULTS, parsed);
    } catch (e) {
      return DEFAULTS;
    }
  }

  function log() {
    if (CFG.show_logs) console.log.apply(console, ['[free-samples]'].concat([].slice.call(arguments)));
  }

  // ---------- Cart helpers ----------
  function getCart() {
    return fetch('/cart.js?ts=' + Date.now(), {
      headers: { Accept: 'application/json' },
      cache: 'no-store',
      credentials: 'same-origin'
    }).then(function (r) {
      if (!r.ok) throw new Error('cart fetch failed');
      return r.json();
    });
  }

  function addSample(variantId, tier) {
    var props = {};
    props[P_SAMPLE] = 'true';
    props[P_CHOICE] = 'true';
    props[P_TIER] = tier;
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1, properties: props }] })
    }).then(function (r) {
      return r.json().then(function (body) {
        if (!r.ok) {
          // Shopify rejected the add (e.g. out of stock, not on Online Store channel).
          // Surface the reason regardless of show_logs — it's an actionable setup error.
          var msg = (body && (body.description || body.message)) || ('HTTP ' + r.status);
          console.warn('[free-samples] /cart/add.js failed for variant ' + variantId + ' (' + tier + '): ' + msg, body);
          var err = new Error(msg);
          err.body = body;
          throw err;
        }
        return body;
      });
    });
  }

  // Remove by line numbers (1-based). Highest line first so indices stay stable.
  function removeLines(lineNumbers) {
    var lines = lineNumbers.slice().sort(function (a, b) { return b - a; });
    var chain = Promise.resolve();
    lines.forEach(function (line) {
      chain = chain.then(function () {
        return fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({ line: line, quantity: 0 })
        });
      });
    });
    return chain;
  }

  function isSampleLine(item) {
    return !!(item.properties && String(item.properties[P_SAMPLE]) === 'true');
  }

  function isTwoMlLine(item) {
    // Defensive: keep the matched 2ml (bundle component) out of the subtotal.
    var t = (item.variant_title || '').toLowerCase();
    return t.indexOf('2ml') !== -1;
  }

  // Product subtotal in pence, PRE-voucher, excluding all sample lines.
  function productSubtotal(cart) {
    return (cart.items || []).reduce(function (sum, item) {
      if (isSampleLine(item) || isTwoMlLine(item)) return sum;
      return sum + (item.original_line_price || 0);
    }, 0);
  }

  function blockedVoucherApplied(cart) {
    var prefixes = (CFG.blocked_code_prefixes || []).map(function (p) { return String(p).toUpperCase(); });
    var values = CFG.blocked_discount_values || [];

    // 1) Code-prefix match (most reliable): MEMREE10- / MEMREE20-.
    var codes = (cart.discount_codes || []).map(function (d) { return String(d.code || ''); });
    var titles = (cart.cart_level_discount_applications || []).map(function (d) { return String(d.title || ''); });
    var applied = codes.concat(titles);
    for (var i = 0; i < applied.length; i++) {
      var code = applied[i].toUpperCase();
      for (var j = 0; j < prefixes.length; j++) {
        if (prefixes[j] && code.indexOf(prefixes[j]) === 0) return true;
      }
    }

    // 2) Value match (secondary): £10 / £20 cart-level discount.
    var apps = cart.cart_level_discount_applications || [];
    for (var k = 0; k < apps.length; k++) {
      if (values.indexOf(apps[k].total_allocated_amount) !== -1) return true;
    }
    if (values.indexOf(cart.total_discount) !== -1 && cart.total_discount > 0) return true;

    return false;
  }

  function computeAllowance(cart) {
    if (blockedVoucherApplied(cart)) return 0;
    var s = productSubtotal(cart);
    var gt = CFG.threshold_strictly_greater;
    var over = function (val, th) { return gt ? val > th : val >= th; };
    if (over(s, CFG.tier2_threshold)) return 2;
    if (over(s, CFG.tier1_threshold)) return 1;
    return 0;
  }

  // Chosen sample lines, with 1-based line numbers, in cart order.
  function chosenSamples(cart) {
    var out = [];
    (cart.items || []).forEach(function (item, i) {
      if (item.properties && String(item.properties[P_CHOICE]) === 'true') {
        out.push({ item: item, line: i + 1 });
      }
    });
    return out;
  }

  function freeChosen(cart) {
    return chosenSamples(cart).filter(function (c) {
      return String(c.item.properties[P_TIER]) === 'free';
    });
  }

  function paidChosen(cart) {
    return chosenSamples(cart).filter(function (c) {
      return String(c.item.properties[P_TIER]) === 'addon';
    });
  }

  // Is the £5 paid route available at all? True when the customer has no free
  // samples available (subtotal below tier 1, or a blocked voucher) or has used
  // the whole free allowance. Never on an empty cart.
  function paidAllowed(cart) {
    if (!cart.item_count) return false;
    var withhold = blockedVoucherApplied(cart);
    if (CFG.voucher_blocks_paid && withhold) return false;
    var allowance = computeAllowance(cart);
    if (allowance === 0) return true;
    return freeChosen(cart).length >= allowance;
  }

  // ---------- Reconciliation ----------
  function reconcile(reason) {
    if (lock) { log('skip (locked):', reason); return Promise.resolve(); }
    lock = true;
    log('reconcile:', reason);

    return getCart().then(function (cart) {
      var withhold = blockedVoucherApplied(cart);
      var allowance = computeAllowance(cart); // 0 when a blocked voucher is applied
      var toRemove = [];

      // Free samples beyond the allowance (covers vouchers and threshold drops) —
      // keep the earliest picks, remove the most recently added first.
      var free = freeChosen(cart);
      if (free.length > allowance) {
        toRemove = free.slice(allowance).map(function (c) { return c.line; });
      }

      // Paid samples are only cleared when configured to block them on a voucher.
      if (withhold && CFG.voucher_blocks_paid) {
        toRemove = toRemove.concat(paidChosen(cart).map(function (c) { return c.line; }));
      }

      if (toRemove.length) {
        log('removing lines', toRemove);
        return removeLines(toRemove).then(function () { return refreshDrawer(); });
      }
      return refreshDrawer();
    }).catch(function (e) {
      log('reconcile error', e);
    }).finally(function () {
      lock = false;
    });
  }

  // ---------- Card interaction ----------
  function onCardToggle(card, btn) {
    if (lock) return;
    var variantId = card.getAttribute('data-variant');
    var tier = card.getAttribute('data-tier'); // 'free' | 'paid'

    setLoading(btn, true);

    // Optimistic UI: flip the selected state immediately so the tick/ADDED shows
    // on tap instead of after the ~5 sequential cart round-trips below. The
    // reconcile chain re-reads the real cart and corrects the card if the server
    // disagrees; the guard/error paths below revert it explicitly.
    var willSelect = !card.classList.contains('is-in-cart');
    card.classList.toggle('is-in-cart', willSelect);

    getCart().then(function (cart) {
      // Already in cart? -> remove it (toggle off).
      var existingLine = findSampleLine(cart, variantId);
      if (existingLine) {
        return removeLines([existingLine]).then(function () { return reconcile('toggle-off'); });
      }

      if (tier === 'free') {
        // Only add a free sample while the allowance has room (the chooser is
        // hidden otherwise, but guard against stale DOM).
        var allowance = computeAllowance(cart);
        if (freeChosen(cart).length >= allowance) { card.classList.remove('is-in-cart'); return; }
        return addSample(variantId, 'free').then(function () { return reconcile('toggle-on-free'); });
      }

      // Paid sample: only when the paid route is available.
      if (!paidAllowed(cart)) { card.classList.remove('is-in-cart'); return; }
      return addSample(variantId, 'addon').then(function () { return reconcile('toggle-on-paid'); });
    }).catch(function (e) {
      log('toggle error', e);
      refreshUI('toggle-error'); // re-sync card state if the add/remove failed
    }).finally(function () {
      setLoading(btn, false);
    });
  }

  // Returns the 1-based line number of a chosen sample matching the given variant id.
  function findSampleLine(cart, variantId) {
    var id = String(variantId);
    var found = null;
    (cart.items || []).forEach(function (item, i) {
      if (found) return;
      if (item.properties && String(item.properties[P_CHOICE]) === 'true' && String(item.variant_id) === id) {
        found = i + 1;
      }
    });
    return found;
  }

  // ---------- Drawer re-render (Section Rendering API) ----------
  function refreshDrawer() {
    var root = (window.Shopify && Shopify.routes && Shopify.routes.root) ? Shopify.routes.root : '/';
    return fetch(root + '?section_id=cart-drawer', { credentials: 'same-origin' })
      .then(function (r) { return r.text(); })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        // Swap cart items (includes the 'Add a sample' button), footer and both choosers.
        swap(doc, '.cart-drawer-items .js-contents, #CartDrawer-CartItems');
        swap(doc, '.cart-drawer__footer');
        swapChoosers(doc);
      })
      .then(function () { return getCart(); })
      .then(function (cart) { updateUI(cart); })
      .catch(function (e) { log('refresh error', e); });
  }

  function swap(doc, selector) {
    var source = doc.querySelector(selector);
    var target = document.querySelector(selector);
    if (source && target) target.innerHTML = source.innerHTML;
  }

  function swapChoosers(doc) {
    [FREE_ID, PAID_ID].forEach(function (id) {
      var source = doc.getElementById(id);
      var target = document.getElementById(id);
      if (source && target) target.replaceWith(source);
    });
  }

  // Light refresh: re-read the cart and re-apply visibility only (no removals).
  function refreshUI(reason) {
    log('refreshUI:', reason);
    return getCart().then(updateUI).catch(function (e) { log('refreshUI error', e); });
  }

  // ---------- UI state ----------
  function updateUI(cart) {
    var freeRoot = document.getElementById(FREE_ID);
    var paidRoot = document.getElementById(PAID_ID);
    var addBtn = document.getElementById(BTN_ID);
    if (!freeRoot && !paidRoot && !addBtn) return;

    bindTracks(); // re-renders replace the track nodes

    var allowance = computeAllowance(cart); // 0 when a blocked voucher is applied
    var freeUsed = freeChosen(cart).length;
    var freeFull = allowance >= 1 && freeUsed >= allowance;
    var cartEmpty = !cart.item_count;
    var paidOk = paidAllowed(cart);

    // The paid chooser can never stay open once the route disappears
    // (e.g. items removed and a free slot opens back up).
    if (!paidOk) paidOpen = false;

    // --- Free chooser: visible while there is unused allowance. Hides when full.
    if (freeRoot) {
      var freeVisible = !cartEmpty && allowance >= 1 && !freeFull;
      freeRoot.hidden = !freeVisible;
      if (freeVisible) {
        var title = freeRoot.querySelector('[data-role="title"]');
        if (title) title.textContent = allowance >= 2 ? CFG.free_heading_two : CFG.free_heading_one;
        var counter = freeRoot.querySelector('[data-role="counter"]');
        if (counter) counter.textContent = freeUsed + ' / ' + allowance + ' ' + CFG.counter_suffix;
        updateCards(freeRoot, cart);
        syncLoop(freeRoot);
      }
    }

    // --- Paid chooser: only when the paid route is available AND the customer opened it.
    if (paidRoot) {
      var paidVisible = paidOk && paidOpen;
      paidRoot.hidden = !paidVisible;
      if (paidVisible) { updateCards(paidRoot, cart); syncLoop(paidRoot); }
    }

    // --- 'Add a sample' button: paid route available, chooser currently closed.
    if (addBtn) addBtn.hidden = !(paidOk && !paidOpen);
  }

  function updateCards(root, cart) {
    root.querySelectorAll('.sample-chooser__card').forEach(function (card) {
      var variantId = card.getAttribute('data-variant');
      var inCart = !!findSampleLine(cart, variantId);
      card.classList.toggle('is-in-cart', inCart);
    });
  }

  function setLoading(btn, on) {
    if (btn) btn.classList.toggle('is-loading', !!on);
  }

  // ---------- Carousel scrolling ----------
  // Ben's design hides the scrollbar, but desktop browsers don't turn a vertical
  // mouse wheel into horizontal scroll and there is no native drag on mouse input.
  // So: wheel scrolls the track horizontally, and mouse-drag scrolls it too (a
  // real drag suppresses the click it would otherwise fire on a card button).
  // Touch devices scroll natively. Re-bound after every re-render via updateUI.
  //
  // Infinite loop + idle drift (8 July 2026): when the set overflows the track,
  // the cards are cloned (aria-hidden, untabbable) and the scroll position wraps
  // inside the middle copy, so scrolling never hits an end. Whenever the pointer
  // is not over the track and nothing inside it has focus, the track drifts
  // slowly (config marquee_speed px/s; 0 disables; prefers-reduced-motion wins;
  // the first touch on a carousel parks the drift for the rest of the session).
  function bindTracks() {
    document.querySelectorAll('.sample-chooser__track').forEach(function (track) {
      if (track.__scrollBound) return;
      track.__scrollBound = true;

      var setWidth = 0;    // width of one full card set incl. gaps; 0 = loop inactive
      var hovering = false;
      var focused = false;
      var pointerDown = false; // any input type — pauses the drift
      var mouseDrag = false;   // mouse drag-to-scroll in progress
      var dragging = false;
      var startX = 0;
      var startLeft = 0;
      var pos = null;      // float scroll position while the drift drives
      var lastT = null;
      var reduceMotion = false;
      try { reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch (err) { /* no-op */ }

      // Clone the card set until the loop can wrap without ever showing a gap.
      function ensureLoop() {
        if (track.__loopDone) return;
        if (track.clientWidth <= 0) return; // still hidden — updateUI calls again once visible
        var cards = track.querySelectorAll('.sample-chooser__card:not([data-clone])');
        if (!cards.length) { track.__loopDone = true; return; }
        var w = 0;
        cards.forEach(function (c) { w += c.offsetWidth + 6; });
        if (w <= track.clientWidth + 1) { track.__loopDone = true; return; } // fits — nothing to loop
        var copies = Math.max(3, Math.ceil((track.clientWidth * 2) / w) + 2);
        for (var i = 1; i < copies; i++) {
          for (var j = 0; j < cards.length; j++) {
            var clone = cards[j].cloneNode(true);
            clone.setAttribute('aria-hidden', 'true');
            clone.setAttribute('data-clone', 'true');
            var btn = clone.querySelector('.sample-chooser__atc');
            if (btn) btn.setAttribute('tabindex', '-1');
            track.appendChild(clone);
          }
        }
        setWidth = w;
        track.classList.add('is-looping');
        track.scrollLeft = w; // start inside the middle copy
        track.__loopDone = true;
      }
      track.__ensureLoop = ensureLoop;

      // Keep the position inside the middle band (seamless wrap). Adjust an
      // in-flight drag and the drift float so neither notices the jump.
      function wrap() {
        if (!setWidth) return;
        var x = track.scrollLeft;
        if (x < setWidth * 0.5) {
          track.scrollLeft = x + setWidth;
          startLeft += setWidth;
          if (pos !== null) pos += setWidth;
        } else if (x > setWidth * 1.5) {
          track.scrollLeft = x - setWidth;
          startLeft -= setWidth;
          if (pos !== null) pos -= setWidth;
        }
      }
      track.addEventListener('scroll', wrap, { passive: true });

      // Idle drift — timestamp-based so the pace is frame-rate independent.
      function tick(t) {
        if (!track.isConnected) return; // node replaced by a re-render — stop this loop
        var speed = Number(CFG.marquee_speed) || 0;
        var idle = setWidth && speed > 0 && !hovering && !focused && !pointerDown
          && !document.hidden && track.clientWidth > 0 && !driftKilled;
        if (idle) {
          if (pos === null || lastT === null) pos = track.scrollLeft;
          else pos += speed * ((t - lastT) / 1000);
          track.scrollLeft = pos;
        } else {
          pos = null;
        }
        lastT = t;
        requestAnimationFrame(tick);
      }
      if (!reduceMotion) requestAnimationFrame(tick);

      track.addEventListener('mouseenter', function () { hovering = true; });
      track.addEventListener('mouseleave', function () { hovering = false; });
      track.addEventListener('focusin', function () { focused = true; });
      track.addEventListener('focusout', function () { focused = false; });

      // Vertical wheel scrolls the carousel horizontally (mouse users).
      track.addEventListener('wheel', function (e) {
        if (track.scrollWidth <= track.clientWidth) return;
        var delta = Math.abs(e.deltaY) > Math.abs(e.deltaX) ? e.deltaY : e.deltaX;
        if (!delta) return;
        e.preventDefault();
        pos = null;
        track.scrollLeft += delta;
      }, { passive: false });

      // Drag to scroll (mouse); a real drag suppresses the following click.
      track.addEventListener('pointerdown', function (e) {
        pointerDown = true; // any input type pauses the drift
        pos = null;
        if (e.pointerType !== 'mouse') {
          driftKilled = true; // any touch parks the carousels for the session
          return;
        }
        if (e.button !== 0) return;
        mouseDrag = true;
        dragging = false;
        startX = e.clientX;
        startLeft = track.scrollLeft;
      });

      track.addEventListener('pointermove', function (e) {
        if (!mouseDrag) return;
        var dx = e.clientX - startX;
        if (!dragging && Math.abs(dx) < 6) return; // click, not drag, until 6px
        if (!dragging) {
          dragging = true;
          track.classList.add('is-dragging');
          try { track.setPointerCapture(e.pointerId); } catch (err) { /* no-op */ }
        }
        track.scrollLeft = startLeft - dx;
        e.preventDefault();
      });

      function endDrag() {
        pointerDown = false;
        if (!mouseDrag) return;
        mouseDrag = false;
        if (dragging) {
          dragging = false;
          track.classList.remove('is-dragging');
          track.__suppressClick = true; // swallow the click this drag generates
          setTimeout(function () { track.__suppressClick = false; }, 120);
        }
      }
      track.addEventListener('pointerup', endDrag);
      track.addEventListener('pointercancel', endDrag);
    });
  }

  // Activate the infinite loop for a chooser that has just become visible.
  function syncLoop(root) {
    var track = root.querySelector('.sample-chooser__track');
    if (track && track.__ensureLoop) track.__ensureLoop();
  }

  // ---------- Event wiring (delegated — survives Section Rendering swaps) ----------
  function onDocumentClick(e) {
    var atc = e.target.closest ? e.target.closest('.sample-chooser__atc') : null;
    if (atc) {
      var dragTrack = atc.closest('.sample-chooser__track');
      if (dragTrack && dragTrack.__suppressClick) { dragTrack.__suppressClick = false; return; }
      e.preventDefault();
      var card = atc.closest('.sample-chooser__card');
      if (card && card.getAttribute('data-available') !== 'false') onCardToggle(card, atc);
      return;
    }

    // Clicking anywhere on the card (not just the ADD TO CART pill) toggles it.
    var cardEl = e.target.closest ? e.target.closest('.sample-chooser__card') : null;
    if (cardEl) {
      var cardTrack = cardEl.closest('.sample-chooser__track');
      if (cardTrack && cardTrack.__suppressClick) { cardTrack.__suppressClick = false; return; }
      e.preventDefault();
      if (cardEl.getAttribute('data-available') !== 'false') {
        onCardToggle(cardEl, cardEl.querySelector('.sample-chooser__atc'));
      }
      return;
    }

    var addBtn = e.target.closest ? e.target.closest('#' + BTN_ID) : null;
    if (addBtn) {
      e.preventDefault();
      paidOpen = true;
      refreshUI('open-paid');
      return;
    }

    var close = e.target.closest ? e.target.closest('.sample-chooser__close') : null;
    if (close) {
      e.preventDefault();
      paidOpen = false;
      refreshUI('close-paid');
    }
  }

  // ---------- Boot ----------
  function boot() {
    if (booted) return;
    booted = true;

    document.addEventListener('click', onDocumentClick);
    bindTracks();
    reconcile('boot');

    // Dawn publishes this on every AJAX cart change.
    try {
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
        subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
          if (event && event.source === 'free-samples') return;
          reconcile('cartUpdate');
        });
      }
    } catch (e) { log('subscribe failed', e); }

    // Also re-evaluate whenever the drawer is opened.
    var drawer = document.querySelector('cart-drawer');
    if (drawer) {
      new MutationObserver(function () {
        if (drawer.classList.contains('active')) reconcile('drawer-open');
      }).observe(drawer, { attributes: true, attributeFilter: ['class'] });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Small debug handle
  window.FreeSamples = { reconcile: reconcile, refreshUI: refreshUI, config: CFG, getCart: getCart };
})();
